import React, { useState, useEffect, useContext } from 'react';
import { LogOut, Search, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayName } from '../../utils/displayName';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const ExternalCollectionUnits = () => {
	const navigate = useNavigate();
	const { logout } = useContext(AppContext);
	const { user } = useAuth();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await adminAPI.getCollectionUnits();
				if (!cancelled) setRows(Array.isArray(data) ? data : []);
			} catch {
				if (!cancelled) {
					toast.error('Failed to load collection units');
					setRows([]);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const q = searchQuery.toLowerCase().trim();
	const filtered = rows.filter((r) => {
		const unit = String(r.collection_unit ?? '');
		const agentName = (r.assigned_agent?.name || '').toLowerCase();
		const agentEmail = (r.assigned_agent?.email || '').toLowerCase();
		if (!q) return true;
		return unit.includes(q) || agentName.includes(q) || agentEmail.includes(q);
	});

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10'>
				<div className='flex justify-between items-center mb-4'>
					<div>
						<h2 className='text-xl font-black uppercase text-white'>Collection units</h2>
						<p className='text-xs text-white/60'>Read-only · {user ? getDisplayName(user) : '—'}</p>
					</div>
					<button type='button' onClick={() => logout && logout()} className='glass-card p-3 rounded-xl'>
						<LogOut className='w-5 h-5 text-white' />
					</button>
				</div>
				<div className='relative'>
					<Search className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50' />
					<input
						type='text'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder='Search by unit number or assigned agent...'
						className='w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 outline-none focus:border-[#9C27B0]/50'
					/>
				</div>
			</header>
			<main className='flex-1 overflow-y-auto p-6 no-scrollbar'>
				<p className='text-xs text-white/50 mb-4'>Each row shows how many bills are in the unit, total owed, and which agent the unit is assigned to (if any).</p>
				{loading ? (
					<p className='text-white/60'>Loading...</p>
				) : filtered.length === 0 ? (
					<p className='text-white/60'>{rows.length === 0 ? 'No collection units found.' : 'No rows match your search.'}</p>
				) : (
					<div className='space-y-3'>
						{filtered.map((r) => (
							<div
								key={String(r.collection_unit)}
								role='button'
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										navigate(`/external/collection-units/${encodeURIComponent(String(r.collection_unit))}`);
									}
								}}
								onClick={() => navigate(`/external/collection-units/${encodeURIComponent(String(r.collection_unit))}`)}
								className='glass-card p-4 border-l-4 border-[#9C27B0] flex flex-col gap-2 cursor-pointer active:scale-[0.99] transition-transform'>
								<div className='flex items-start gap-3'>
									<div className='w-10 h-10 bg-[#9C27B0]/15 rounded-xl flex items-center justify-center shrink-0'>
										<MapPin className='w-5 h-5 text-[#CE93D8]' />
									</div>
									<div className='flex-1 min-w-0'>
										<p className='text-white font-black text-lg'>Unit {r.collection_unit}</p>
										<p className='text-xs text-white/60'>
											{r.bill_count != null ? `${r.bill_count} bills` : '—'}
											{r.total_owed != null ? ` · Total owed: ${Number(r.total_owed).toLocaleString()}` : ''}
										</p>
										<p className='text-sm text-white mt-2'>
											<span className='text-white/50 text-xs uppercase font-bold'>Assigned to </span>
											{r.assigned_agent?.name ? (
												<>
													{r.assigned_agent.name}
													{r.assigned_agent.email ? (
														<span className='text-white/50 text-xs block mt-0.5'>{r.assigned_agent.email}</span>
													) : null}
												</>
											) : (
												<span className='text-amber-200/90'>Unassigned</span>
											)}
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</main>
		</div>
	);
};

export default ExternalCollectionUnits;
